import React, { useState } from 'react'
import { Platform, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useDispatch } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import type { StackNavigationProp } from '@react-navigation/stack'
import { NavigationContainer } from '@screens/Common/containers'
import { Button, Text } from '@src/components/base'
import { LoadingIndicator } from '@src/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useDispatchAsync, useGoBack, useSnack } from '@src/utils'
import { OnboardingStackParamsList } from '@src/navigation/types'
import { Analytics, CustomEventTypes } from '@src/config'
import { useByosProducts } from '@src/screens/Marketplace/utils/hooks'

export const LibreIndirectSyncConfirmationScreen = () => {
  const styles = useStyleSheet(themedStyles)
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const navigation = useNavigation()
  const onboardingNavigation = useNavigation<StackNavigationProp<OnboardingStackParamsList>>()
  const goBack = useGoBack()
  const showSnack = useSnack()
  const byosProducts = useByosProducts()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const healthAppName = Platform.OS === 'ios' ? 'Apple Health' : 'Health Connect'

  const onConfirmPress = async () => {
    if (isSubmitting) {
      return
    }

    Analytics.track(CustomEventTypes.OwnSensorLibreIndirectSyncConfirmed)

    setIsSubmitting(true)
    try {
      await dispatchAsync({
        type: 'app/confirmLibreIndirectSync',
      })
      dispatch({
        type: 'uiFlags/updateUiFlags',
        payload: { showLibreIndirectSyncConfirmation: false },
      })
      onboardingNavigation.navigate('OwnSensorFreeTrialSubscriptionOffer', {
        product: byosProducts[0],
      })
    } catch {
      showSnack('Unable to confirm right now. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const onBackPress = () => {
    if (navigation.canGoBack()) {
      return goBack()
    }

    onboardingNavigation.replace('OnboardingSubscriptionOptions')
  }

  return (
    <NavigationContainer
      title="Connect Your Libre Data"
      leftIcon={{ pack: 'eva', name: 'arrow-back' }}
      goBack={onBackPress}
    >
      <SafeAreaView style={styles.content} edges={['bottom']}>
        <View style={styles.mainContent}>
          <Text type="title-3" style={styles.title}>
            To sync your Libre glucose data, you need a third-party app.
          </Text>

          <Text type="regular" style={styles.bodyText}>
            Use an app that can sync from Libre apps, like GotCGM or xDrip+.
          </Text>

          <Text type="regular" style={styles.bodyText}>
            In that app, enable writing to {healthAppName}.
          </Text>

          <Text type="regular" style={styles.bodyText}>
            Then enable {healthAppName} in Nutrisense so your glucose data can be imported into
            Nutrisense.
          </Text>
        </View>

        <View style={styles.buttons}>
          <Button
            type="primary"
            size="block"
            accessibilityLabel="I understand"
            onPress={onConfirmPress}
            accessoryRight={isSubmitting ? <LoadingIndicator size="small" /> : null}
            disabled={isSubmitting}
          >
            I understand
          </Button>
        </View>
      </SafeAreaView>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  content: {
    flex: 1,
    paddingHorizontal: 16,
    backgroundColor: 'theme.background',
  },
  mainContent: {
    flex: 1,
    paddingTop: 16,
  },
  title: {
    marginBottom: 24,
    textAlign: 'center',
  },
  bodyText: {
    marginBottom: 16,
    textAlign: 'center',
  },
  buttons: {
    marginBottom: 16,
  },
})
